import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Stack, Title, Text, Badge, Group, Button } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useT } from '../../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Security panel. Since 3 October 2026 the user list carries whether the account is switched on,
 * whether it has two-factor sign-in and whether its address was confirmed (an invitation accepted), and
 * an account can be switched off and back on here. Sessions and last sign-in are still unknown to the
 * console; force-reset and revoke-sessions stay disabled with their reason.
 */
export function SecurityTab({ user }: { user?: admin.AdminUser }) {
  const t = useT();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const active = user?.is_active !== false;
  const toggle = useMutation({
    mutationFn: () => admin.setUserActive(user!.id, !active),
    onSuccess: () => {
      notifySuccess(t(active ? 'users.view.security.disabled' : 'users.view.security.enabled', { email: user!.email }), t('users.view.security.actions.title'));
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) => notifyError(
      isApiError(err) && err.code === 'LAST_PLATFORM_ADMIN' ? t('users.view.security.lastAdmin')
      : isApiError(err) && err.code === 'CANNOT_SUSPEND_SELF' ? t('users.view.security.notSelf')
      : (err as Error).message, t('users.view.security.actions.title'))
  });
  const yesNo = (v: boolean | undefined) => v === undefined
    ? <Badge size="xs" variant="outline">{t('common.unknown')}</Badge>
    : <Badge size="xs" color={v ? 'green' : 'gray'} variant="light">{t(v ? 'common.yes' : 'common.no')}</Badge>;

  function confirmToggle() {
    openConfirmModal({
      title: t(active ? 'users.view.security.disableTitle' : 'users.view.security.enableTitle'),
      children: <Text fz="sm">{t(active ? 'users.view.security.disableBody' : 'users.view.security.enableBody', { email: user?.email ?? '' })}</Text>,
      labels: { confirm: t(active ? 'users.view.security.actions.disable' : 'users.view.security.actions.enable'), cancel: t('action.cancel') },
      confirmProps: { color: active ? 'red' : undefined },
      onConfirm: () => toggle.mutate()
    });
  }

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('users.view.security.title')}</Title>
          <Stack gap="sm">
            <Row label={t('users.view.security.status')} value={
              <Badge size="xs" color={active ? 'green' : 'red'} variant="light" data-testid="user-active">{t(active ? 'users.view.security.statusOn' : 'users.view.security.statusOff')}</Badge>} />
            <Row label={t('users.view.security.emailVerified')} value={yesNo(user?.email_confirmed)} />
            <Row label={t('users.view.security.twoFactor')} value={yesNo(user?.two_factor_enabled)} />
            <Row label={t('users.view.security.activeSessions')} value={<Badge size="xs" variant="outline">{t('common.unknown')}</Badge>} />
            <Row label={t('users.view.security.lastSignIn')} value={<Badge size="xs" variant="outline">{t('common.unknown')}</Badge>} />
          </Stack>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('users.view.security.actions.title')}</Title>
          <Group gap="sm" wrap="wrap">
            <Button size="xs" variant="outline" color={active ? 'red' : undefined} disabled={!user || user.id === me?.id}
              loading={toggle.isPending} onClick={confirmToggle} data-testid="user-toggle-active">
              {t(active ? 'users.view.security.actions.disable' : 'users.view.security.actions.enable')}
            </Button>
            <PendingAction label={t('users.view.security.actions.reset')} tooltip={t('users.view.security.notAvailable.reset')} />
            <PendingAction label={t('users.view.security.actions.revoke')} tooltip={t('users.view.security.notAvailable.revoke')} />
          </Group>
          <Text fz="xs" c={MUTED}>{t('users.view.security.disableExplainer')}</Text>
          <Text fz="xs" c={MUTED}>{t('users.view.security.notAvailable.explainer')}</Text>
          <Text fz="xs" c={MUTED}>{t('users.view.security.actions.auditNote')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}

/** The reason is in the body text as well as the tooltip, because a tooltip on a disabled button reaches nobody using a keyboard. */
function PendingAction({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip label={tooltip}>
      <span style={{ display: 'inline-flex' }}>
        <Button size="xs" variant="outline" disabled>{label}</Button>
      </span>
    </Tooltip>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <Group gap="xs" align="baseline">{value}</Group>
    </div>
  );
}
